import { useState } from 'react'
import { SyntheticEvent } from 'react'

// Flags
import { useFlags } from 'launchdarkly-react-client-sdk'

// Context
import { useUser } from 'app/User'

// Types
import { Surgery } from 'common/types'
import { ProcedureOptionsMenuProps } from './ProcedureOptionsMenu.types'

// Utils
import { deriveSurgeryStatus } from 'lib/utils/data'
import _ from 'lodash'

export const useProcedureOptionsMenuLogic = (
  props: ProcedureOptionsMenuProps
) => {
  const {
    surgery,
    onEditProcedure,
    onCancelProcedure,
    onAssignPreferenceCard,
    onUnassignPreferenceCard,
  } = props

  const flags = useFlags()
  const { isRep } = useUser()

  const [optionsMenuAnchor, setOptionsMenuAnchor] =
    useState<HTMLElement | null>(null)

  const handleOpenOptionsMenu = (anchorElement: HTMLElement) => {
    setOptionsMenuAnchor(anchorElement)
  }

  const handleCloseOptionsMenu = (event?: Event | SyntheticEvent | {}) => {
    if (
      event &&
      'stopPropagation' in event &&
      typeof event.stopPropagation === 'function'
    ) {
      event.stopPropagation()
    }
    setOptionsMenuAnchor(null)
  }

  const handleEditProcedure = () => {
    if (surgery && onEditProcedure) {
      onEditProcedure(surgery)
    }
    handleCloseOptionsMenu()
  }

  const handleCancelProcedure = () => {
    if (surgery && onCancelProcedure) {
      onCancelProcedure(surgery)
    }
    handleCloseOptionsMenu()
  }

  const handleAssignPreference = () => {
    if (!surgery) return

    if (onAssignPreferenceCard) {
      onAssignPreferenceCard(surgery)
    }
    handleCloseOptionsMenu()
  }

  const canCancelSurgery = (surgery: Surgery | null): boolean => {
    if (!surgery) return false

    const surgeryStatus = deriveSurgeryStatus({
      statusName: surgery?.status?.name,
      assetGroups: _.uniq(surgery?.assetGroups),
    })

    return (
      !surgeryStatus.hasImplantableHardware &&
      !surgeryStatus.hasImplantableBiologic &&
      !surgeryStatus.hasImplantableOther &&
      surgeryStatus.implantableScans.length === 0
    )
  }

  const canAssignPreference = (surgery: Surgery): boolean => {
    return surgery?.preferenceCardId === null
  }

  const handleUnassignPreference = (surgery: Surgery) => {
    if (!surgery) return

    if (onUnassignPreferenceCard) {
      onUnassignPreferenceCard(surgery)
    }

    handleCloseOptionsMenu()
  }

  return {
    flags,
    isRep,
    optionsMenuAnchor,
    handleOpenOptionsMenu,
    handleCloseOptionsMenu,
    handleEditProcedure,
    handleCancelProcedure,
    handleAssignPreference,
    canCancelSurgery,
    canAssignPreference,
    handleUnassignPreference,
  }
}
